import { Fragment } from "react";
import type { LucideIcon } from "lucide-react";
import {
  Hammer, Wrench, Trash2, Truck, Wind, Zap, Droplets, Trees, Settings, ArrowRight,
  Store, Briefcase, Scissors, Dumbbell, UtensilsCrossed, Stethoscope,
} from "lucide-react";
import { LocaleLink } from "../i18n/LocaleLink";
import { Container } from "../components/ui";
import { MotionProvider, Reveal, PromoBanner, Button as DSButton, PageHeading, SectionHeading } from "../ds/core";
import { industriesFor } from "../content/industries";
import { useLocale } from "../i18n/useLocale";
import { cmsImage } from "../i18n/cmsImage";
import { TRIAL_PATH as TRIAL } from "../config/entrypoints";

const UNS = "https://images.unsplash.com";
const PH_FIELD = "https://framerusercontent.com/images/gvrZUoowU6t5rwpwtONkkVWoFSU.png";
const PH_CLEAN = `${UNS}/photo-1581578731548-c64695cc6952?w=900&auto=format&fit=crop&q=80`;
const PH_CONSTR = `${UNS}/photo-1504307651254-35680f356dfd?w=900&auto=format&fit=crop&q=80`;


const ICONS: Record<string, LucideIcon> = {
  Hammer, Wrench, Trash2, Truck, Wind, Zap, Droplets, Trees, Settings,
  Store, Briefcase, Scissors, Dumbbell, UtensilsCrossed, Stethoscope,
};

const ICON_KEYS: Record<string, string> = {
  construction: "Hammer", cleaning: "Trash2", installation: "Wrench",
  "field-service": "Truck", hvac: "Wind", electricians: "Zap",
  plumbing: "Droplets", landscaping: "Trees", maintenance: "Settings",
  // Non-technical trades
  retail: "Store", offices: "Briefcase", beauty: "Scissors",
  wellness: "Dumbbell", hospitality: "UtensilsCrossed", healthcare: "Stethoscope",
};

// Photo tiles woven into the bento, inserted after these industry indices.
const PHOTO_AFTER: Record<number, string> = { 1: PH_CLEAN, 4: PH_CONSTR, 7: PH_FIELD };
// Alt-text i18n keys per mosaic photo (keyed to the same grid positions as PHOTO_AFTER).
const PHOTO_ALT_KEY: Record<number, string> = {
  1: "industries:bento.photo.cleaning.alt",
  4: "industries:bento.photo.construction.alt",
  7: "industries:bento.photo.fieldService.alt",
};

// Compliance highlight cards (Belgium only) — legal-scheme labels stay verbatim
// (glossary), the rest is i18n. Each entry maps to flat keys in the catalog.
const COMPLIANCE_CARDS = [
  { label: "Check-in at work", labelKey: "compliance.card.ddd.label", titleKey: "compliance.card.ddd.title", bodyKey: "compliance.card.ddd.body", slug: "digitaal-in-en-uitchecken-verplicht-op-bouwwerven-vanaf-2025" },
  { label: "CIAO@work", labelKey: "compliance.card.ciao.label", titleKey: "compliance.card.ciao.title", bodyKey: "compliance.card.ciao.body", slug: "checkin-work-ciao-verplichtingen-fouten-en-tips-voor-kmo-s-in-belgie" },
  { label: "2027 law", labelKey: "compliance.card.law2027.label", titleKey: "compliance.card.law2027.title", bodyKey: "compliance.card.law2027.body", slug: "verplichte-arbeidstijdregistratie-in-belgie-wat-moeten-bedrijven-weten" },
] as const;

export default function Industries() {
  const { t, locale, market } = useLocale();
  const industries = industriesFor(locale);
  const isBelgium = market === "belgium";
  const blurbSuffix = isBelgium ? ".be" : ".intl";

  return (
    <>
      {/* 1. Blue hero — heading + description */}
      <PageHeading
        eyebrow={t("industries:hero.eyebrow")}
        title={t("industries:hero.title")}
        intro={isBelgium
          ? t("industries:hero.intro.be")
          : t("industries:hero.intro.intl")}
      />

      {/* 2. All industries — bento (white block, cards + photos, animated icons) */}
      <MotionProvider>
        <section className="bg-bg">
          <Container className="py-16">
            <SectionHeading
              eyebrow={t("industries:bento.eyebrow")}
              title={t("industries:bento.title")}
              intro={isBelgium
                ? t("industries:bento.intro.be")
                : t("industries:bento.intro.intl")}
            />
            <div className="mt-10 rounded-3xl border border-line bg-surface p-2">
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {industries.map((ind, i) => {
                  // Fall back rather than render `undefined`: an industry added
                  // to the content files without an ICON_KEYS entry would
                  // otherwise throw "Element type is invalid" and fail the whole
                  // static build, not just this tile.
                  const Icon = ICONS[ICON_KEYS[ind.slug]] ?? Settings;
                  const photo = PHOTO_AFTER[i];
                  return (
                    <Fragment key={ind.slug}>
                      <Reveal className="flex">
                        <LocaleLink
                          to={`/industry/${ind.slug}`}
                          className="group flex h-full w-full flex-col rounded-2xl border border-transparent bg-bg-subtle p-5 transition-colors hover:border-line hover:bg-white"
                        >
                          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-soft text-primary transition-transform duration-300 ease-out group-hover:-rotate-6 group-hover:scale-110">
                            <Icon size={20} strokeWidth={2} />
                          </span>
                          <h3 className="mt-4 font-display text-ds-18 font-semibold text-ink">{ind.name}</h3>
                          <p className="mt-2 flex-1 text-ds-14 leading-relaxed text-ink-soft">{t(`industries:bento.blurb.${ind.slug}${blurbSuffix}`)}</p>
                          <span className="mt-4 inline-flex items-center gap-1 text-[13px] font-semibold text-primary opacity-0 transition-opacity group-hover:opacity-100">
                            {t("industries:bento.readMore")} <ArrowRight size={14} strokeWidth={2} />
                          </span>
                        </LocaleLink>
                      </Reveal>
                      {photo && (
                        <Reveal delay={0.05} className="flex">
                          <div className="relative w-full min-h-[180px] overflow-hidden rounded-2xl">
                            <img src={cmsImage(t, PHOTO_ALT_KEY[i].replace(/\.alt$/, ""), photo)} alt={t(PHOTO_ALT_KEY[i])} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 hover:scale-105" />
                          </div>
                        </Reveal>
                      )}
                    </Fragment>
                  );
                })}
              </div>
            </div>
          </Container>
        </section>
      </MotionProvider>

      {/* Compliance highlight — Belgium only */}
      {isBelgium && (
        <section className="bg-bg-subtle">
          <Container className="py-14">
            <div className="grid gap-8 md:grid-cols-3">
              {COMPLIANCE_CARDS.map((c) => (
                <LocaleLink
                  key={c.label}
                  to={`/blog/${c.slug}`}
                  className="group flex flex-col rounded-2xl border border-line bg-white p-4 md:p-6 transition-colors hover:border-primary/40"
                >
                  <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-accent">{t(`industries:${c.labelKey}`)}</span>
                  <h3 className="mt-2 font-display text-ds-18 font-semibold text-ink">{t(`industries:${c.titleKey}`)}</h3>
                  <p className="mt-2 flex-1 text-ds-14 leading-relaxed text-ink-soft">{t(`industries:${c.bodyKey}`)}</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-[13px] font-semibold text-primary opacity-0 transition-opacity group-hover:opacity-100">
                    {t("industries:compliance.readArticle")} <ArrowRight size={14} strokeWidth={2} />
                  </span>
                </LocaleLink>
              ))}
            </div>
          </Container>
        </section>
      )}

      {/* 3. Don't see your sector — DS PromoBanner, gold tone */}
      <PromoBanner
        tone="gold"
        eyebrow={t("industries:promo.eyebrow")}
        title={t("industries:promo.title")}
        body={t("industries:promo.body")}
        action={<DSButton to={TRIAL} variant="solid-light">{t("industries:promo.cta")}</DSButton>}
      />
    </>
  );
}

